import { FC } from 'react'
import { ContentCommentRow } from 'src/services/contentAnalysisApi/interfaces/contentAnalysisV2.interface'
import styles from './commentRow.module.scss'
import {
  formatCount,
  initialOf,
  platformLabel,
  shortName,
  timeAgo,
} from '../shared'

interface CommentRowProps {
  comment: ContentCommentRow
  // The list page mixes analyses, so each row says which one it is from.
  showCampaign?: boolean
}

const SENTIMENT_LABEL = {
  negative: 'Negative',
  neutral: 'Neutral',
  positive: 'Positive',
}

/**
 * One comment: avatar initial, the text, then "Maia K. · Instagram · 611 likes
 * · 58 replies · 3 days ago", a sentiment pill and Open. Open goes to the
 * comment itself when the platform gives it a link, else to its post.
 */
export const CommentRow: FC<CommentRowProps> = ({
  comment,
  showCampaign = false,
}) => {
  const href = comment.commentUrl || comment.postUrl || undefined
  // Unknown counts (older data, platforms that hide them) are left out rather
  // than shown as 0.
  const meta = [
    shortName(comment.author),
    platformLabel(comment.platform),
    typeof comment.likes === 'number'
      ? `${formatCount(comment.likes)} ${comment.likes === 1 ? 'like' : 'likes'}`
      : null,
    typeof comment.replyCount === 'number'
      ? `${formatCount(comment.replyCount)} ${
          comment.replyCount === 1 ? 'reply' : 'replies'
        }`
      : null,
    timeAgo(comment.timestamp),
  ].filter(Boolean)

  return (
    <div className={styles.row}>
      <span aria-hidden="true" className={styles.avatar}>
        {initialOf(comment.author)}
      </span>
      <div className={styles.body}>
        <p className={styles.text} title={comment.content}>
          {comment.content}
        </p>
        <div className={styles.meta}>
          {comment.isReply && <span className={styles.replyTag}>Reply</span>}
          <span className={styles.metaText}>{meta.join(' · ')}</span>
          {showCampaign && !!comment.analysisTitle && (
            <span className={styles.campaign} title={comment.analysisTitle}>
              {comment.analysisTitle}
            </span>
          )}
        </div>
      </div>
      <div className={styles.actions}>
        {comment.sentiment && (
          <span className={`${styles.sentiment} ${styles[comment.sentiment]}`}>
            {SENTIMENT_LABEL[comment.sentiment]}
          </span>
        )}
        {href ? (
          <a
            className={styles.open}
            href={href}
            rel="noopener noreferrer"
            target="_blank"
            title={
              comment.commentUrl ? 'Open the comment' : 'Open the post it is on'
            }
          >
            Open
          </a>
        ) : (
          <span className={`${styles.open} ${styles.openDisabled}`}>Open</span>
        )}
      </div>
    </div>
  )
}
